// frontend/src/setupFetchWithCredentials.js
// Ensures every API call automatically carries credentials + CSRF token headers.
// Comments explain the security intent; they do not weaken defenses because
// built assets never expose the original source comments to attackers.

import { isReadOnlySafePath, READ_ONLY_SAFE_PREFIXES } from './shared/maintenancePaths';
import {
  MAX_GET_RETRY_ATTEMPTS,
  getRetryDelayMs,
  isSafeRetryMethod,
  shouldRetryGetError,
  shouldRetryGetResponse,
} from './utils/idempotentRetry';
import { NETWORK_GUIDANCE_MESSAGE, isLikelyNetworkError } from './utils/networkErrorMessage';
import { resolveArtistDemoFetch } from './utils/artistDemoRuntime';

const FETCH_PATCH_VERSION = '2026-07-20-artist-demo-runtime';

function normalizeBase(url) {
  if (!url) return '';
  return url.replace(/\/+$/, '');
}

function safeOrigin(url) {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch (_) {
    return null;
  }
}

function normalizeHeaderValue(value) {
  if (value == null) return '';
  return String(value).trim();
}

function toPositiveInt(value, fallback, min = 1, max = Number.MAX_SAFE_INTEGER) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const floored = Math.floor(parsed);
  if (floored < min) return min;
  if (floored > max) return max;
  return floored;
}

function stableSerialize(input) {
  if (input == null) return 'null';
  if (typeof input === 'string') return input;
  if (typeof input === 'number' || typeof input === 'boolean') return String(input);
  if (Array.isArray(input)) {
    return `[${input.map((item) => stableSerialize(item)).join(',')}]`;
  }
  if (typeof input === 'object') {
    const keys = Object.keys(input).sort();
    const parts = keys.map((key) => `${JSON.stringify(key)}:${stableSerialize(input[key])}`);
    return `{${parts.join(',')}}`;
  }
  try {
    return JSON.stringify(input);
  } catch (_) {
    return String(input);
  }
}

// cyrb53 hash (small and fast)
function hashString53(value) {
  const text = String(value || '');
  let h1 = 0xdeadbeef ^ text.length;
  let h2 = 0x41c6ce57 ^ text.length;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const result = 4294967296 * (2097151 & h2) + (h1 >>> 0);
  return result.toString(36);
}

function getHeaderValue(headers, name) {
  const target = String(name || '').toLowerCase();
  if (!target || !headers) return '';
  if (headers instanceof Headers) {
    return normalizeHeaderValue(headers.get(name));
  }
  if (Array.isArray(headers)) {
    const found = headers.find((entry) => {
      if (!Array.isArray(entry) || entry.length < 2) return false;
      return String(entry[0] || '').toLowerCase() === target;
    });
    return normalizeHeaderValue(found ? found[1] : '');
  }
  if (typeof headers === 'object') {
    const direct = Object.keys(headers).find((key) => String(key).toLowerCase() === target);
    return normalizeHeaderValue(direct ? headers[direct] : '');
  }
  return '';
}

function setHeader(headers, name, value) {
  if (!value) return headers;
  if (headers instanceof Headers) {
    headers.set(name, value);
    return headers;
  }
  if (Array.isArray(headers)) {
    const filtered = headers.filter(([key]) => String(key).toLowerCase() !== String(name).toLowerCase());
    filtered.push([name, value]);
    return filtered;
  }
  return { ...(headers || {}), [name]: value };
}

function nativeAuthBridgeHeaders() {
  if (typeof window === 'undefined') return [];
  const bridge = window.__CASTICART_NATIVE_AUTH__;
  if (!bridge || typeof bridge !== 'object') return [];

  const headers = [];
  const authorization = normalizeHeaderValue(
    bridge.authorizationHeader || bridge.authorization || bridge.Authorization
  );
  if (/^Bearer\s+\S+/i.test(authorization)) {
    headers.push(['Authorization', authorization]);
  }

  const client = normalizeHeaderValue(bridge.client || 'native');
  const app = normalizeHeaderValue(bridge.app || 'casticart_admin_mac');
  const platform = normalizeHeaderValue(bridge.platform || 'macos');
  const deviceId = normalizeHeaderValue(bridge.deviceId || bridge.deviceID);
  if (client) headers.push(['x-casticart-client', client]);
  if (app) headers.push(['x-casticart-app', app]);
  if (platform) headers.push(['x-casticart-platform', platform]);
  if (deviceId) headers.push(['x-casticart-device-id', deviceId]);
  return headers;
}

function applyNativeAuthBridgeHeaders(headers) {
  return nativeAuthBridgeHeaders().reduce(
    (nextHeaders, [name, value]) => setHeader(nextHeaders, name, value),
    headers
  );
}

function canonicalizeSearchParams(searchParams) {
  const pairs = Array.from(searchParams.entries()).sort((a, b) => {
    if (a[0] !== b[0]) return a[0].localeCompare(b[0]);
    return a[1].localeCompare(b[1]);
  });
  return pairs
    .map(([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
    .join('&');
}

function normalizeBodyForFingerprint(body, contentType = '') {
  if (body == null) return '';
  const normalizedContentType = String(contentType || '').toLowerCase();

  if (typeof body === 'string') {
    const trimmed = body.trim();
    if (normalizedContentType.includes('application/json')) {
      try {
        return stableSerialize(JSON.parse(trimmed));
      } catch (_) {
        // noop
      }
    }
    if (trimmed.length > 4096) {
      return `${trimmed.slice(0, 2048)}::len=${trimmed.length}`;
    }
    return trimmed;
  }

  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
    return canonicalizeSearchParams(body);
  }

  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    const lines = [];
    body.forEach((val, key) => {
      if (typeof File !== 'undefined' && val instanceof File) {
        lines.push(`${key}=file:${val.name}:${val.size}:${val.type || ''}`);
      } else {
        lines.push(`${key}=text:${String(val)}`);
      }
    });
    lines.sort();
    return lines.join('&');
  }

  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return `blob:${body.type || ''}:${Number(body.size || 0)}`;
  }

  if (body instanceof ArrayBuffer) {
    return `arraybuffer:${body.byteLength}`;
  }

  if (ArrayBuffer.isView(body)) {
    return `typedarray:${body.byteLength}`;
  }

  if (typeof body === 'object') {
    return stableSerialize(body);
  }

  return String(body);
}

function patchFetchWithCredentials() {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  if (
    window.__FETCH_WITH_CREDENTIALS_PATCHED__ &&
    window.__FETCH_WITH_CREDENTIALS_PATCH_VERSION__ === FETCH_PATCH_VERSION
  ) {
    return;
  }

  const apiBase = normalizeBase(process.env.REACT_APP_API_URL || '');
  const apiOrigin = safeOrigin(apiBase);
  const sameOrigin = window.location?.origin || null;
  const nativeFetch = (window.__CASTICART_NATIVE_FETCH__ || window.fetch).bind(window);
  window.__CASTICART_NATIVE_FETCH__ = nativeFetch;
  const CSRF_COOKIE_NAME = process.env.REACT_APP_CSRF_COOKIE || 'casticart-csrf';
  const CSRF_HEADER_NAME = process.env.REACT_APP_CSRF_HEADER || 'x-casticart-csrf';
  // presence を明示管理するクライアントであることをサーバへ伝える。これにより backend の
  // activityHeartbeat は暗黙 touch をスキップし、在席は presence heartbeat/away のみで決まる。
  const PRESENCE_MANAGED_HEADER = 'x-casticart-presence-managed';
  const IDEMPOTENCY_HEADER_NAME = process.env.REACT_APP_IDEMPOTENCY_HEADER || 'Idempotency-Key';
  const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
  const IDEMPOTENCY_REUSE_WINDOW_MS = toPositiveInt(
    process.env.REACT_APP_IDEMPOTENCY_REUSE_WINDOW_MS,
    10 * 60 * 1000,
    1000,
    24 * 60 * 60 * 1000
  );
  const IDEMPOTENCY_REPLAY_MAX_BYTES = toPositiveInt(
    process.env.REACT_APP_IDEMPOTENCY_REPLAY_MAX_BYTES,
    512 * 1024,
    1024,
    5 * 1024 * 1024
  );
  const IDEMPOTENCY_STORE_MAX_ITEMS = toPositiveInt(
    process.env.REACT_APP_IDEMPOTENCY_STORE_MAX_ITEMS,
    256,
    32,
    1024
  );
  const IDEMPOTENCY_STORAGE_KEY = 'casticart:auto-idempotency:v1';
  const IDEMPOTENCY_AUTO_BYPASS_PATHS = [
    /^\/api\/event-chat(?:\/|$)/,
    /^\/api\/admin\/messages(?:\/|$)/,
    /^\/api\/artist\/messages(?:\/|$)/,
    /^\/api\/events\/[^/]+\/messages(?:\/|$)/,
    /^\/api\/common\/events\/casting-summary$/,
    /^\/api\/common\/events\/task-summary$/,
    /^\/api\/admin\/playguide-connections\/[^/]+\/test$/,
    // presence の heartbeat/away は自然冪等で高頻度。自動冪等キー/インフライト再生の対象外にする。
    /^\/api\/presence(?:\/|$)/,
  ];
  const autoIdempotencyCache = new Map();
  const inFlightMutationRequests = new Map();
  let autoIdempotencyCacheLoaded = false;
  let csrfBootstrapPromise = null;

  const readCookie = (name) => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  };

  const resolveCsrfEndpoint = (targetUrl) => {
    try {
      const resolved = new URL(targetUrl, sameOrigin || undefined);
      return `${resolved.origin}/api/csrf-token`;
    } catch (_) {
      return apiBase ? `${apiBase}/api/csrf-token` : '/api/csrf-token';
    }
  };

  const fetchFreshCsrfToken = (targetUrl) =>
    nativeFetch(resolveCsrfEndpoint(targetUrl), {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    }).then(async (res) => {
      let token = readCookie(CSRF_COOKIE_NAME);
      try {
        const data = await res.clone().json();
        token = normalizeHeaderValue(data?.token) || token;
      } catch (_) {
        // Keep the cookie-derived token when the body is not JSON.
      }
      return token || readCookie(CSRF_COOKIE_NAME);
    });

  const bootstrapCsrfToken = (targetUrl, { forceRefresh = false } = {}) => {
    const existing = readCookie(CSRF_COOKIE_NAME);
    if (existing && !forceRefresh) return Promise.resolve(existing);
    if (csrfBootstrapPromise) return csrfBootstrapPromise;

    csrfBootstrapPromise = fetchFreshCsrfToken(targetUrl)
      .catch(() => readCookie(CSRF_COOKIE_NAME))
      .finally(() => {
        csrfBootstrapPromise = null;
      });

    return csrfBootstrapPromise;
  };

  const isCsrfFailureResponse = async (response) => {
    if (!response || response.status !== 403) return false;
    try {
      const data = await response.clone().json();
      const message = `${data?.message || ''} ${data?.error?.code || ''}`;
      return message.toLowerCase().includes('csrf');
    } catch (_) {
      return false;
    }
  };

  const shouldAttachCredentials = (targetUrl) => {
    if (!targetUrl) return false;
    try {
      const resolved = new URL(targetUrl, sameOrigin || undefined);

      // Any request that targets the configured API origin should include cookies.
      if (apiOrigin && resolved.origin === apiOrigin) {
        return true;
      }

      // Fallback: requests aimed at the current origin + /api should also include cookies.
      if (sameOrigin && resolved.origin === sameOrigin && resolved.pathname.startsWith('/api')) {
        return true;
      }

      return false;
    } catch (_) {
      return false;
    }
  };

  const hasNativeAuthorization = () =>
    nativeAuthBridgeHeaders().some(([name, value]) => {
      return String(name || '').toLowerCase() === 'authorization' && /^Bearer\s+\S+/i.test(String(value || ''));
    });

  const shouldAttachNativeAuth = (targetUrl) => {
    if (!targetUrl || !hasNativeAuthorization()) return false;
    try {
      const resolved = new URL(targetUrl, sameOrigin || undefined);
      if (resolved.pathname !== '/api' && !resolved.pathname.startsWith('/api/')) return false;

      if (apiOrigin && resolved.origin === apiOrigin) return true;
      if (sameOrigin && resolved.origin === sameOrigin) return true;

      const hostname = String(resolved.hostname || '').toLowerCase();
      return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
    } catch (_) {
      return false;
    }
  };

  const canonicalizeRequestPath = (targetUrl) => {
    try {
      const resolved = new URL(targetUrl, sameOrigin || undefined);
      const query = canonicalizeSearchParams(resolved.searchParams);
      return `${resolved.pathname}${query ? `?${query}` : ''}`;
    } catch (_) {
      return String(targetUrl || '');
    }
  };

  const shouldAutoBypassIdempotency = (requestPath) => {
    const pathname = String(requestPath || '').split('?')[0] || '';
    return IDEMPOTENCY_AUTO_BYPASS_PATHS.some((pattern) => pattern.test(pathname));
  };

  const pruneAutoIdempotencyCache = () => {
    const now = Date.now();
    for (const [fingerprintHash, entry] of autoIdempotencyCache.entries()) {
      if (!entry || !Number.isFinite(entry.expiresAt) || entry.expiresAt <= now) {
        autoIdempotencyCache.delete(fingerprintHash);
      }
    }
    if (autoIdempotencyCache.size <= IDEMPOTENCY_STORE_MAX_ITEMS) return;
    const sorted = Array.from(autoIdempotencyCache.entries()).sort((a, b) => {
      const aa = Number(a[1]?.expiresAt || 0);
      const bb = Number(b[1]?.expiresAt || 0);
      return bb - aa;
    });
    sorted.slice(IDEMPOTENCY_STORE_MAX_ITEMS).forEach(([fingerprintHash]) => {
      autoIdempotencyCache.delete(fingerprintHash);
    });
  };

  const loadAutoIdempotencyCache = () => {
    if (autoIdempotencyCacheLoaded) return;
    autoIdempotencyCacheLoaded = true;
    try {
      const raw = sessionStorage.getItem(IDEMPOTENCY_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      parsed.forEach((row) => {
        if (!Array.isArray(row) || row.length < 2) return;
        const fingerprintHash = String(row[0] || '');
        const entry = row[1] || {};
        const idempotencyKey = normalizeHeaderValue(entry.idempotencyKey);
        const expiresAt = Number(entry.expiresAt);
        if (!fingerprintHash || !idempotencyKey || !Number.isFinite(expiresAt)) return;
        autoIdempotencyCache.set(fingerprintHash, {
          idempotencyKey,
          expiresAt,
        });
      });
      pruneAutoIdempotencyCache();
    } catch (_) {
      autoIdempotencyCache.clear();
    }
  };

  const persistAutoIdempotencyCache = () => {
    try {
      pruneAutoIdempotencyCache();
      sessionStorage.setItem(
        IDEMPOTENCY_STORAGE_KEY,
        JSON.stringify(Array.from(autoIdempotencyCache.entries()))
      );
    } catch (_) {
      // noop
    }
  };

  const createAutoIdempotencyKey = (fingerprintHash) => {
    const randomPart =
      typeof window.crypto?.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
    return `auto:${fingerprintHash}:${randomPart}`.slice(0, 150);
  };

  const resolveAutoIdempotency = ({ method, requestPath, body, contentType }) => {
    loadAutoIdempotencyCache();
    const bodyFingerprint = normalizeBodyForFingerprint(body, contentType);
    const material = `${method}|${requestPath}|${bodyFingerprint}`;
    const fingerprintHash = hashString53(material);
    const now = Date.now();
    const cached = autoIdempotencyCache.get(fingerprintHash);
    if (cached && cached.idempotencyKey && Number(cached.expiresAt) > now) {
      return {
        fingerprintHash,
        idempotencyKey: cached.idempotencyKey,
      };
    }
    const idempotencyKey = createAutoIdempotencyKey(fingerprintHash);
    autoIdempotencyCache.set(fingerprintHash, {
      idempotencyKey,
      expiresAt: now + IDEMPOTENCY_REUSE_WINDOW_MS,
    });
    persistAutoIdempotencyCache();
    return {
      fingerprintHash,
      idempotencyKey,
    };
  };

  const getFingerprintBody = (input, init) => {
    if (init && Object.prototype.hasOwnProperty.call(init, 'body')) {
      return init.body;
    }
    return null;
  };

  const getFingerprintBodyAsync = (input, init) => {
    if (init && Object.prototype.hasOwnProperty.call(init, 'body')) {
      return Promise.resolve(init.body);
    }
    if (input instanceof Request) {
      return input.clone().text().catch(() => null);
    }
    return Promise.resolve(null);
  };

  const captureResponseSnapshot = async (response) => {
    if (!(response instanceof Response)) return null;
    const contentLength = Number(response.headers.get('content-length'));
    if (Number.isFinite(contentLength) && contentLength > IDEMPOTENCY_REPLAY_MAX_BYTES) {
      return null;
    }
    try {
      const clone = response.clone();
      const bodyBuffer = await clone.arrayBuffer();
      if (bodyBuffer.byteLength > IDEMPOTENCY_REPLAY_MAX_BYTES) return null;
      return {
        status: clone.status,
        statusText: clone.statusText,
        headers: Array.from(clone.headers.entries()),
        bodyBuffer,
      };
    } catch (_) {
      return null;
    }
  };

  const buildReplayResponse = (snapshot) => {
    if (!snapshot) return null;
    const headers = new Headers(snapshot.headers || []);
    headers.set('X-Idempotent-Replay', '1');
    const bodyBuffer = snapshot.bodyBuffer instanceof ArrayBuffer ? snapshot.bodyBuffer.slice(0) : null;
    return new Response(bodyBuffer, {
      status: Number(snapshot.status) || 200,
      statusText: snapshot.statusText || '',
      headers,
    });
  };

  const buildInProgressResponse = () => {
    const body = JSON.stringify({
      success: false,
      code: 'REQUEST_IN_PROGRESS',
      message: '同一内容のリクエストを処理中です。完了までお待ちください。',
    });
    return new Response(body, {
      status: 409,
      statusText: 'Conflict',
      headers: { 'Content-Type': 'application/json' },
    });
  };

  const dispatchToast = (text, type = 'error', duration) => {
    if (!text) return;
    try {
      window.dispatchEvent(new CustomEvent('app:toast', { detail: { type, text, duration } }));
    } catch (_) {
      // noop
    }
  };

  // ネットワーク起因の失敗（fetch reject）を統一文言でユーザーへ薄く通知する。
  // 連続失敗・並行リクエストでの氾濫を避けるため一定間隔にスロットルする。
  // AbortError（呼び出し側キャンセル）や HTTP エラー応答は対象外。
  const NETWORK_TOAST_THROTTLE_MS = 8000;
  let lastNetworkToastAt = 0;
  const maybeDispatchNetworkToast = (error) => {
    if (!isLikelyNetworkError(error)) return;
    const now = Date.now();
    if (now - lastNetworkToastAt < NETWORK_TOAST_THROTTLE_MS) return;
    lastNetworkToastAt = now;
    dispatchToast(NETWORK_GUIDANCE_MESSAGE);
  };

  const withNetworkToast = (promise) =>
    promise.catch((error) => {
      maybeDispatchNetworkToast(error);
      throw error;
    });

  const READ_ONLY_SAFE_PATHS = [
    ...READ_ONLY_SAFE_PREFIXES,
    // Auth/reset系（READ_ONLY中でも許可）
    '/api/admin-login',
    '/api/admin-login/2fa',
    '/api/admin-login/2fa/resend',
    '/api/admin-logout',
    '/api/admin/forgot-password',
    '/api/admin/password-reset',
    '/api/artist-login',
    '/api/artist-logout',
    '/api/artist-password-reset',
  ];

  const shouldBlockForMaintenance = (targetUrl, method = 'GET') => {
    const upper = (method || 'GET').toUpperCase();
    // 読み取り系はブロック対象外
    if (upper === 'GET' || upper === 'HEAD' || upper === 'OPTIONS') return false;

    // メンテナンス状態を参照（MaintenanceContext から同期される）
    const state = window.__CASTICART_MAINTENANCE_STATE;
    if (!state || !state.isMaintenanceActive || !state.isReadOnly) return false;

    try {
      const resolved = new URL(targetUrl, sameOrigin || undefined);
      const path = resolved.pathname || '';
      // メンテナンス設定系エンドポイントはブロックしない
      if (path.startsWith('/api/dev') || path.startsWith('/api/system/maintenance')) {
        return false;
      }
      // 読み取り専用モード中でも、安全な集計系やログイン/リセット系は許可する
      if (READ_ONLY_SAFE_PATHS.some((p) => path.startsWith(p)) || isReadOnlySafePath(path)) return false;
    } catch (_) {
      // URL 解析に失敗した場合は念のためブロックしない
      return false;
    }

    return true;
  };

  const handleMaintenanceToast = (response, method = 'GET') => {
    const upper = (method || 'GET').toUpperCase();
    // 通知するのは更新系メソッドのみ
    if (upper === 'GET' || upper === 'HEAD' || upper === 'OPTIONS') return;
    if (!response || (response.status !== 423 && response.status !== 503)) return;

    const fallback =
      response.status === 423
        ? 'メンテナンス中です。現在は読み取り専用のため更新できません。'
        : 'メンテナンス中です。しばらくお待ちください。';

    // JSON レスポンスなら message を参照、それ以外はフォールバック
    try {
      const clone = response.clone();
      const contentType = clone.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        dispatchToast(fallback);
        return;
      }
      clone
        .json()
        .then((data) => {
          const msg = data?.message || fallback;
          dispatchToast(msg);
        })
        .catch(() => dispatchToast(fallback));
    } catch (_) {
      dispatchToast(fallback);
    }
  };

  // 過渡的な通信障害（ネットワーク断・502/504）に対する冪等GET限定リトライ。
  // Abort シグナルを尊重しつつ、待機は途中中断できるようにする。
  const sleepWithAbort = (ms, signal) =>
    new Promise((resolve) => {
      if (!ms) {
        resolve();
        return;
      }
      if (signal?.aborted) {
        resolve();
        return;
      }
      let timer = null;
      const onAbort = () => {
        if (timer) clearTimeout(timer);
        signal?.removeEventListener?.('abort', onAbort);
        resolve();
      };
      timer = setTimeout(() => {
        signal?.removeEventListener?.('abort', onAbort);
        resolve();
      }, ms);
      signal?.addEventListener?.('abort', onAbort, { once: true });
    });

  // GET/HEAD/OPTIONS かつ 502/504 / ネットワークエラー のときだけ再送する。
  // 変更系・4xx(429含む)・500・503・423・AbortError は分類ロジック側で除外される。
  const runWithGetRetry = async (executeFetch, method, signal) => {
    let attempt = 0;
    for (;;) {
      try {
        const response = await executeFetch();
        if (
          attempt < MAX_GET_RETRY_ATTEMPTS &&
          !signal?.aborted &&
          shouldRetryGetResponse({ method, status: response?.status })
        ) {
          await sleepWithAbort(getRetryDelayMs(attempt), signal);
          if (signal?.aborted) return response;
          attempt += 1;
          continue;
        }
        return response;
      } catch (error) {
        if (
          attempt < MAX_GET_RETRY_ATTEMPTS &&
          !signal?.aborted &&
          shouldRetryGetError({ method, error })
        ) {
          await sleepWithAbort(getRetryDelayMs(attempt), signal);
          if (signal?.aborted) throw error;
          attempt += 1;
          continue;
        }
        throw error;
      }
    }
  };

  const dispatchFetch = (input, init, url, method) => {
    // メンテナンス（読み取り専用）中の更新系リクエストはクライアント側でブロックし、仮想レスポンスを返す
    if (url && shouldBlockForMaintenance(url, method)) {
      const body = JSON.stringify({
        success: false,
        message: 'メンテナンス中です。現在は読み取り専用のため更新できません。',
      });
      const fake = new Response(body, {
        status: 423,
        statusText: 'Locked',
        headers: { 'Content-Type': 'application/json' },
      });
      handleMaintenanceToast(fake, method);
      return Promise.resolve(fake);
    }

    const attachCredentials = shouldAttachCredentials(url);
    const attachNativeAuth = shouldAttachNativeAuth(url);

    if (!attachCredentials && !attachNativeAuth) {
      return nativeFetch(input, init).then((res) => {
        handleMaintenanceToast(res, method);
        return res;
      });
    }

    const upperMethod = String(method || 'GET').toUpperCase();
    const requestPath = canonicalizeRequestPath(url);
    const baseHeaders =
      init?.headers || (input instanceof Request ? input.headers : undefined);
    const explicitIdempotencyKey = normalizeHeaderValue(
      getHeaderValue(baseHeaders, IDEMPOTENCY_HEADER_NAME)
    );
    const mutationRequest = MUTATION_METHODS.has(upperMethod);
    const autoBypass =
      mutationRequest && !explicitIdempotencyKey && shouldAutoBypassIdempotency(requestPath);
    const shouldUseIdempotency = mutationRequest && !autoBypass;

    const runRequest = (bodyForFingerprint = null) => {
      let effectiveIdempotencyKey = explicitIdempotencyKey;
      if (shouldUseIdempotency && !effectiveIdempotencyKey) {
        const contentType = getHeaderValue(baseHeaders, 'content-type');
        const resolved = resolveAutoIdempotency({
          method: upperMethod,
          requestPath,
          body: bodyForFingerprint,
          contentType,
        });
        effectiveIdempotencyKey = resolved.idempotencyKey;
      }

      const executionKey =
        shouldUseIdempotency && effectiveIdempotencyKey
          ? `${upperMethod}|${requestPath}|${effectiveIdempotencyKey}`
          : '';

      const sendRequest = (activeCsrfToken = readCookie(CSRF_COOKIE_NAME)) => {
        if (input instanceof Request) {
          let headers = new Headers(baseHeaders || undefined);
          if (activeCsrfToken) {
            headers.set(CSRF_HEADER_NAME, activeCsrfToken);
          }
          headers.set(PRESENCE_MANAGED_HEADER, '1');
          if (shouldUseIdempotency && effectiveIdempotencyKey) {
            headers.set(IDEMPOTENCY_HEADER_NAME, effectiveIdempotencyKey);
          }
          if (attachNativeAuth) {
            headers = applyNativeAuthBridgeHeaders(headers);
          }
          const cloned = new Request(input, {
            ...(init || {}),
            credentials: init?.credentials || (attachCredentials || attachNativeAuth ? 'include' : undefined),
            headers,
          });
          return nativeFetch(cloned).then((res) => {
            handleMaintenanceToast(res, upperMethod);
            return res;
          });
        }

        const nextInit = { ...(init || {}) };
        if (!nextInit.credentials && (attachCredentials || attachNativeAuth)) {
          nextInit.credentials = 'include';
        }
        nextInit.headers = setHeader(baseHeaders, CSRF_HEADER_NAME, activeCsrfToken);
        nextInit.headers = setHeader(nextInit.headers, PRESENCE_MANAGED_HEADER, '1');
        if (shouldUseIdempotency && effectiveIdempotencyKey) {
          nextInit.headers = setHeader(nextInit.headers, IDEMPOTENCY_HEADER_NAME, effectiveIdempotencyKey);
        }
        if (attachNativeAuth) {
          nextInit.headers = applyNativeAuthBridgeHeaders(nextInit.headers);
        }
        return nativeFetch(input, nextInit).then((res) => {
          handleMaintenanceToast(res, upperMethod);
          return res;
        });
      };

      const sendWithCsrfRetry = () => {
        if (mutationRequest && !readCookie(CSRF_COOKIE_NAME)) {
          return bootstrapCsrfToken(url).then((token) => sendRequest(token));
        }
        return sendRequest().then(async (response) => {
          if (!mutationRequest || !(await isCsrfFailureResponse(response))) {
            return response;
          }
          const refreshedToken = await bootstrapCsrfToken(url, { forceRefresh: true });
          if (!refreshedToken) return response;
          return sendRequest(refreshedToken);
        });
      };

      if (!executionKey) {
        return sendWithCsrfRetry();
      }

      const existing = inFlightMutationRequests.get(executionKey);
      if (existing?.snapshotPromise) {
        return existing.snapshotPromise
          .then((snapshot) => buildReplayResponse(snapshot) || buildInProgressResponse())
          .catch(() => buildInProgressResponse());
      }

      const requestPromise = sendWithCsrfRetry();
      const snapshotPromise = requestPromise
        .then((response) => captureResponseSnapshot(response))
        .catch(() => null);
      inFlightMutationRequests.set(executionKey, { snapshotPromise });

      return requestPromise.finally(() => {
        const current = inFlightMutationRequests.get(executionKey);
        if (current && current.snapshotPromise === snapshotPromise) {
          inFlightMutationRequests.delete(executionKey);
        }
      });
    };

    if (shouldUseIdempotency && !explicitIdempotencyKey) {
      const bodyForFingerprint = input instanceof Request
        ? getFingerprintBodyAsync(input, init)
        : Promise.resolve(getFingerprintBody(input, init));
      return bodyForFingerprint.then((body) => runRequest(body));
    }

    return runRequest();
  };

  window.fetch = (input, init = {}) => {
    const artistDemoResponse = resolveArtistDemoFetch(input, init);
    if (artistDemoResponse) return Promise.resolve(artistDemoResponse);

    const url =
      typeof input === 'string'
        ? input
        : input && typeof input.url === 'string'
          ? input.url
          : '';

    const method =
      (init && init.method) ||
      (input && input.method) ||
      (typeof input === 'object' && input instanceof Request && input.method) ||
      'GET';

    const upperMethod = String(method || 'GET').toUpperCase();

    // 冪等な安全読み取り（GET/HEAD/OPTIONS）だけを過渡障害リトライの対象にする。
    // それ以外（変更系）は一切リトライせず従来どおり1回だけ実行する（データ二重実行防止）。
    if (!isSafeRetryMethod(upperMethod)) {
      return withNetworkToast(dispatchFetch(input, init, url, method));
    }

    const signal =
      (init && init.signal) ||
      (input instanceof Request ? input.signal : undefined) ||
      undefined;
    return withNetworkToast(
      runWithGetRetry(() => dispatchFetch(input, init, url, method), upperMethod, signal)
    );
  };

  window.__FETCH_WITH_CREDENTIALS_PATCHED__ = true;
  window.__FETCH_WITH_CREDENTIALS_PATCH_VERSION__ = FETCH_PATCH_VERSION;
}

patchFetchWithCredentials();
